import { useState, useRef, useEffect } from "react";
import { Send, Loader2, Sparkles, MessageCircle, Lock, Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ExpertChat({ manualId, expert, isOwner, onTopup }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [used, setUsed] = useState(expert?.used || 0);
  const [quota, setQuota] = useState(expert?.quota || 0);
  const unlimited = !!expert?.unlimited;
  const [remaining, setRemaining] = useState(unlimited ? null : (expert?.remaining ?? 0));
  const [paused, setPaused] = useState(!unlimited && (expert?.remaining ?? 0) <= 0);
  const [topupBusy, setTopupBusy] = useState(false);
  const sessionId = useRef(`exp-${Math.random().toString(36).slice(2)}-${Date.now()}`);
  const scrollRef = useRef(null);
  const label = expert?.label || "Ask the Expert";

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const ask = async () => {
    const q = input.trim();
    if (!q || busy || paused) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: q }]);
    setInput("");
    setBusy(true);
    try {
      const res = await api.post(`/manuals/${manualId}/expert/chat`, {
        message: q,
        history,
        session_id: sessionId.current,
      });
      const reply = (res.data?.reply || "").trim() || "Sorry, I didn't catch that — try again?";
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
      if (!res.data?.unlimited) {
        setUsed(res.data.used);
        setQuota(res.data.quota);
        setRemaining(res.data.remaining);
        if (res.data.remaining <= 0) setPaused(true);
      }
    } catch (e) {
      if (e?.response?.status === 402) {
        setPaused(true);
        setRemaining(0);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "I'm having trouble right now — please try again in a moment." },
        ]);
      }
    } finally {
      setBusy(false);
    }
  };

  const topup = async () => {
    if (topupBusy) return;
    setTopupBusy(true);
    try {
      const { data } = await api.post(`/manuals/${manualId}/expert/enable`);
      setQuota(data.expert.quota);
      setUsed(data.expert.used);
      setRemaining(data.expert.remaining);
      setPaused(false);
      toast.success("Added 500 more questions — the Expert is live again!");
      onTopup && onTopup();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add questions — check your balance.");
    } finally {
      setTopupBusy(false);
    }
  };

  return (
    <div
      data-testid="expert-chat"
      className="mt-3 rounded-2xl border-2 border-[#1e3aaa] overflow-hidden shadow-sm"
      style={{ background: "#0b0b10" }}
    >
      <div className="flex items-center justify-between gap-2 px-4 py-3 bg-[#1e3aaa] text-white">
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-white/15 shrink-0">
            <MessageCircle size={16} />
          </span>
          <div className="leading-tight min-w-0">
            <div className="font-extrabold text-sm tracking-wide truncate" data-testid="expert-chat-title">
              {label}
            </div>
            <div className="text-[11px] text-white/75">Powered by AI • on-topic answers</div>
          </div>
        </div>
        {!unlimited && (
          <span
            data-testid="expert-chat-tally"
            className="shrink-0 tabular-nums text-[11px] font-extrabold bg-black/25 rounded-full px-2.5 py-1 border border-white/20"
            title="Questions used on this video"
          >
            {used} / {quota}
          </span>
        )}
      </div>

      <div className="px-4 py-3">
        {paused ? (
          <div className="text-[13px] text-[#c9d2f0] text-center py-2" data-testid="expert-chat-paused">
            <Lock size={18} className="mx-auto mb-2 text-[#6f8cff]" />
            {isOwner ? (
              <>
                <p className="font-semibold text-white">This Expert has answered all {quota} questions.</p>
                <p className="mt-1 text-[#9aa6d0]">Add another 500 to keep viewers chatting.</p>
                <button
                  data-testid="expert-chat-topup"
                  onClick={topup}
                  disabled={topupBusy}
                  className="mt-3 inline-flex items-center gap-1.5 font-extrabold text-white px-4 py-2 rounded-full bg-[#1e3aaa] border-2 border-[#16308c] hover:scale-105 transition-transform disabled:opacity-60"
                >
                  {topupBusy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add 500 more — $1.99
                </button>
              </>
            ) : (
              <p>The Expert is taking a break — check back soon!</p>
            )}
          </div>
        ) : (
          <>
            {messages.length === 0 ? (
              <div className="text-[13px] text-[#c9d2f0]" data-testid="expert-chat-intro">
                <p className="flex items-start gap-1.5">
                  <Sparkles size={14} className="text-[#6f8cff] mt-0.5 shrink-0" />
                  Curious about this video? I'm your on-topic expert — ask me anything about it.
                </p>
              </div>
            ) : (
              <div
                ref={scrollRef}
                data-testid="expert-chat-messages"
                className="max-h-72 overflow-y-auto flex flex-col gap-2 pr-1"
              >
                {messages.map((m, i) => (
                  <div
                    key={i}
                    data-testid={`expert-msg-${m.role}`}
                    className={`max-w-[88%] text-[13px] leading-relaxed rounded-2xl px-3 py-2 whitespace-pre-line ${
                      m.role === "user"
                        ? "self-end bg-[#1e3aaa] text-white rounded-br-sm"
                        : "self-start bg-black border border-[#1e3aaa]/60 text-[#e7ecff] rounded-bl-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                ))}
                {busy && (
                  <div className="self-start bg-black border border-[#1e3aaa]/60 text-[#6f8cff] rounded-2xl rounded-bl-sm px-3 py-2">
                    <Loader2 size={15} className="animate-spin" />
                  </div>
                )}
              </div>
            )}

            <div className="mt-3 flex items-center gap-2">
              <input
                data-testid="expert-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && ask()}
                placeholder="Ask the Expert about this video…"
                disabled={busy}
                className="flex-1 text-[13px] rounded-full border-2 border-[#1e3aaa] bg-black px-4 py-2.5 text-white placeholder:text-[#8a93b8] outline-none focus:ring-2 focus:ring-[#1e3aaa]/40 disabled:opacity-60"
              />
              <button
                data-testid="expert-chat-send"
                onClick={ask}
                disabled={busy || !input.trim()}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-[#1e3aaa] text-white border-2 border-[#16308c] hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100 shrink-0"
                aria-label="Send"
              >
                {busy ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
